import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Zap, Flame, ChevronRight, Flag, Clock, TrendingUp } from "lucide-react";
import PageLayout from "../../components/layout/PageLayout";
import TrendingBar from "../../components/football/TrendingBar";
import SuperBowlCountdown from "../../components/football/SuperBowlCountdown";
import NewsCard from "../../components/football/NewsCard";
import { HERO_IMAGES } from "../../lib/constants";
import api from "../../lib/api";

const SECTIONS = [
    { to: "/football/fantasy",   title: "FANTASY FOOTBALL", desc: "10 owners. One league. One champion.", icon: Trophy },
    { to: "/football/nfl",       title: "NFL",              desc: "Live scores, standings, headlines.",    icon: Flag },
    { to: "/football/college",   title: "D1 COLLEGE",       desc: "AP Top 25, CFP, transfer portal.",       icon: Flame },
    { to: "/football/high-school", title: "HIGH SCHOOL TOP 50", desc: "The 50 programs every fan should know.", icon: TrendingUp },
];

export default function FootballHome() {
    const [news, setNews] = useState([]);
    const [scoreboard, setScoreboard] = useState({ events: [], week: null });
    const [hs, setHs] = useState([]);
    const [owners, setOwners] = useState([]);
    const [poll, setPoll] = useState(null);

    useEffect(() => {
        api.get("/football/news", { params: { limit: 6 } }).then(r => setNews(r.data)).catch(() => {});
        api.get("/football/nfl/scoreboard").then(r => setScoreboard(r.data)).catch(() => {});
        api.get("/football/hs/rankings").then(r => setHs(r.data.slice(0, 5))).catch(() => {});
        api.get("/football/fantasy/owners").then(r => setOwners(r.data.slice(0, 5))).catch(() => {});
        api.get("/football/college/rankings").then(r => setPoll(r.data.polls?.[0])).catch(() => {});
    }, []);

    return (
        <PageLayout>
            {/* HERO */}
            <section className="relative min-h-[80vh] flex items-end overflow-hidden pt-24" data-testid="football-home-hero">
                <img src={HERO_IMAGES.stadium_lights} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
                <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 pb-14 md:pb-20 w-full">
                    <div className="section-label mb-5">Digital Beast Football</div>
                    <h1 className="font-display text-5xl sm:text-7xl md:text-8xl text-white leading-[0.88] tracking-tight max-w-5xl" data-testid="football-hero-headline">
                        FROM FRIDAY NIGHT LIGHTS<br/>TO <span className="text-[#d4af37]">SUPER BOWL SUNDAY.</span>
                    </h1>
                    <p className="mt-6 max-w-3xl text-white/70 text-lg leading-relaxed">
                        Follow America's top high school programs, D1 college football, NFL action, fantasy football and the athletes becoming tomorrow's Digital Beasts.
                    </p>
                    <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3">
                        {SECTIONS.map(s => (
                            <Link key={s.to} to={s.to} className="surface surface-hover p-5 group" data-testid={`football-hero-cta-${s.title.toLowerCase().replace(/\s+/g,'-')}`}>
                                <s.icon className="text-[#d4af37]" size={22} />
                                <div className="font-display text-lg md:text-xl text-white mt-4">{s.title}</div>
                                <div className="text-white/50 text-xs mt-1">{s.desc}</div>
                                <ChevronRight className="text-white/30 group-hover:text-[#d4af37] mt-3 transition-colors" size={16} />
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <TrendingBar />

            {/* COMMAND CENTER */}
            <section className="py-14 md:py-20" data-testid="football-command-center">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-6">
                    {/* Left: NFL scoreboard + news */}
                    <div className="lg:col-span-8 space-y-6">
                        <div>
                            <div className="flex items-end justify-between mb-4">
                                <div>
                                    <div className="section-label mb-2">Live · NFL Scoreboard</div>
                                    <h2 className="font-display text-3xl md:text-4xl text-white tracking-tight">
                                        Week {scoreboard.week || "—"}
                                    </h2>
                                </div>
                                <Link to="/football/nfl" className="btn-ghost text-xs">All games <ChevronRight size={12} /></Link>
                            </div>
                            <div className="grid md:grid-cols-2 gap-3">
                                {scoreboard.events.slice(0, 6).map(e => (
                                    <div key={e.id} className="surface p-4" data-testid={`scoreboard-event-${e.id}`}>
                                        <div className="flex justify-between text-[10px] uppercase tracking-widest text-white/40 mb-3">
                                            <span>{e.status}</span>
                                            <span>{e.venue?.slice(0, 22)}</span>
                                        </div>
                                        {e.teams.map(t => (
                                            <div key={t.id} className="flex items-center gap-3 py-1.5">
                                                {t.logo && <img src={t.logo} alt="" className="w-6 h-6" />}
                                                <div className="flex-1">
                                                    <div className="text-white text-sm">{t.name}</div>
                                                    <div className="text-white/40 text-[10px]">{t.record}</div>
                                                </div>
                                                <div className="font-display text-2xl text-white">{t.score || "—"}</div>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                                {scoreboard.events.length === 0 && (
                                    <div className="surface p-6 text-white/40 text-sm md:col-span-2">No live games right now — check schedules on the <Link to="/football/nfl" className="text-[#d4af37] underline">NFL hub</Link>.</div>
                                )}
                            </div>
                        </div>

                        {/* Latest news */}
                        <div>
                            <div className="flex items-end justify-between mb-4">
                                <div>
                                    <div className="section-label mb-2">Today's Football News</div>
                                    <h2 className="font-display text-3xl md:text-4xl text-white tracking-tight">Headlines</h2>
                                </div>
                                <Link to="/football/news" className="btn-ghost text-xs">All news <ChevronRight size={12} /></Link>
                            </div>
                            <div className="grid md:grid-cols-2 gap-3">
                                {news.map(n => <NewsCard key={n.id} item={n} testid={`home-news-${n.id}`} />)}
                            </div>
                        </div>
                    </div>

                    {/* Right sidebar */}
                    <aside className="lg:col-span-4 space-y-4">
                        <SuperBowlCountdown />

                        {/* HS Top 5 */}
                        <div className="surface p-6" data-testid="home-hs-top5">
                            <div className="section-label mb-4">High School · Top 5</div>
                            <div className="space-y-3">
                                {hs.map(t => (
                                    <Link key={t.id} to={`/football/high-school/${t.slug}`} className="flex items-center gap-3 group">
                                        <div className="font-display text-3xl text-[#d4af37] w-9">{t.rank}</div>
                                        <div className="flex-1 border-b border-white/10 pb-3">
                                            <div className="text-white text-sm group-hover:text-[#d4af37]">{t.school}</div>
                                            <div className="text-white/40 text-xs">{t.city}, {t.state} · {t.record}</div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                            <Link to="/football/high-school" className="btn-ghost text-xs mt-4">See Top 50 <ChevronRight size={12} /></Link>
                        </div>

                        {/* AP Top 5 */}
                        {poll && (
                            <div className="surface p-6" data-testid="home-ap-top5">
                                <div className="section-label mb-4">{poll.name} · Top 5</div>
                                <div className="space-y-3">
                                    {poll.ranks.slice(0, 5).map(r => (
                                        <div key={r.current} className="flex items-center gap-3 border-b border-white/10 pb-3">
                                            <div className="font-display text-3xl text-[#d4af37] w-9">{r.current}</div>
                                            {r.logo && <img src={r.logo} alt="" className="w-8 h-8" />}
                                            <div className="flex-1">
                                                <div className="text-white text-sm">{r.team}</div>
                                                <div className="text-white/40 text-xs">{r.record}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Fantasy leader */}
                        <div className="surface p-6" data-testid="home-fantasy-leader">
                            <div className="section-label mb-4">Fantasy · Top 5</div>
                            <div className="space-y-3">
                                {owners.map(o => (
                                    <Link key={o.id} to={`/football/fantasy/owners/${o.slug}`} className="flex items-center gap-3 group">
                                        <div className="font-display text-3xl text-[#d4af37] w-9">{o.league_rank}</div>
                                        <div className="flex-1 border-b border-white/10 pb-3">
                                            <div className="text-white text-sm group-hover:text-[#d4af37]">{o.team_name}</div>
                                            <div className="text-white/40 text-xs">{o.name} · {o.record_wins}-{o.record_losses}</div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                            <Link to="/football/fantasy" className="btn-ghost text-xs mt-4">Fantasy clubhouse <ChevronRight size={12} /></Link>
                        </div>
                    </aside>
                </div>
            </section>
        </PageLayout>
    );
}
